import { useCallback, useEffect, useRef, useState } from "react";
import { WIDGET_ORIGIN } from "../utils/widgetOriginConfig";
import { WIDGET_MESSAGE_TYPES } from "../services/widgetPreviewMessages";

const DEBOUNCE_MS = 150;
const READY_TIMEOUT_MS = 5000;

/**
 * Manage a postMessage handshake with the widget preview iframe.
 *
 * Usage:
 *   const { iframeRef, reloadKey, isReady, error, reload } =
 *     useWidgetPreview({ config, previewState, invite });
 *   <iframe ref={iframeRef} key={reloadKey} ... />
 *
 * The hook:
 *  - sends PREVIEW_INIT once after the iframe sends READY
 *  - sends PREVIEW_CONFIG_UPDATE on every `config` change, debounced 150ms
 *  - sends PREVIEW_STATE_CHANGED on every `previewState` or `invite` change.
 *    `invite` is the invite card's sample text, "" for none. The caller
 *    debounces it: this channel is the immediate one, and a keystroke that
 *    reached the widget would restart the card's reveal on every letter.
 *  - exposes an `error` if READY does not arrive in 5s
 *  - exposes `reload()` to hard-reset the iframe — the consumer MUST pass
 *    `reloadKey` to the iframe's `key` prop so React remounts it; otherwise
 *    the iframe element stays alive, the widget never re-boots (single-shot
 *    PREVIEW_INIT contract), and the 5s timeout fires over a working preview.
 *
 * Strict origin discipline: only listens to messages from WIDGET_ORIGIN and
 * posts only to WIDGET_ORIGIN. Never uses "*".
 */
export function useWidgetPreview({ config, previewState, invite }) {
  const iframeRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Latest-prop refs so the READY listener always sends the current
  // config/previewState — not whatever was captured the last time the
  // effect ran. Without these, reloading the iframe after the user has
  // typed sends a stale INIT (the post-INIT debounced CONFIG_UPDATE
  // would patch it up, but there's a visible flash) and rapid state
  // toggles during the boot window are dropped.
  const configRef = useRef(config);
  const previewStateRef = useRef(previewState);
  const inviteRef = useRef(invite);
  configRef.current = config;
  previewStateRef.current = previewState;
  inviteRef.current = invite;

  // READY listener + initial INIT send
  useEffect(() => {
    setIsReady(false);
    setError(null);

    const timeout = setTimeout(() => {
      setError("Preview did not initialise in 5 seconds.");
    }, READY_TIMEOUT_MS);

    const onMessage = (e) => {
      if (e.origin !== WIDGET_ORIGIN) return;
      if (e.data?.type !== WIDGET_MESSAGE_TYPES.READY) return;
      clearTimeout(timeout);
      setIsReady(true);
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: WIDGET_MESSAGE_TYPES.PREVIEW_INIT,
          payload: {
            config: configRef.current,
            state: previewStateRef.current,
            invite: inviteRef.current || undefined,
          },
          version: 1,
        },
        WIDGET_ORIGIN,
      );
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timeout);
    };
    // Re-run only on reloadKey. config + previewState are read via refs
    // above so we don't re-arm the listener (which would re-INIT) on
    // every keystroke or state click.
  }, [reloadKey]);

  // CONFIG updates (debounced)
  useEffect(() => {
    if (!isReady) return;
    const t = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: WIDGET_MESSAGE_TYPES.PREVIEW_CONFIG_UPDATE,
          payload: { config },
          version: 1,
        },
        WIDGET_ORIGIN,
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [config, isReady]);

  // STATE changes (immediate, no debounce — clicks are infrequent, and the
  // invite text arrives here already debounced by the caller)
  useEffect(() => {
    if (!isReady) return;
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: WIDGET_MESSAGE_TYPES.PREVIEW_STATE_CHANGED,
        payload: { state: previewState, invite: invite || undefined },
        version: 1,
      },
      WIDGET_ORIGIN,
    );
  }, [previewState, invite, isReady]);

  // Clear stale ready/error eagerly so the overlay never lingers between
  // the click and React running the next effect. Memoized so consumers can
  // depend on stable identity (e.g. button onClick, future useEffect deps).
  const reload = useCallback(() => {
    setIsReady(false);
    setError(null);
    setReloadKey((c) => c + 1);
  }, []);

  return { iframeRef, reloadKey, isReady, error, reload };
}
